/**
 * Desktop flyout state for nav groups. Returns the open-group key, the
 * anchored position to render the panel at, and a ref-callback consumers
 * use to register the button each group renders.
 *
 * Closes on outside click, Escape, and route changes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export function useNavFlyout() {
  const location = useLocation();
  const [flyoutGroup, setFlyoutGroup] = useState(null);
  const [flyoutPos, setFlyoutPos] = useState(null);
  const flyoutRef = useRef(null);
  const groupButtonRefs = useRef({});

  // The desktop flyout is rendered with position:fixed so it can escape the
  // sidebar nav's overflow clipping. Recompute its anchor whenever the open
  // group, viewport size, or sidebar scroll changes.
  useEffect(() => {
    if (!flyoutGroup) {
      setFlyoutPos(null);
      return;
    }
    const update = () => {
      const btn = groupButtonRefs.current[flyoutGroup];
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      setFlyoutPos({ top: r.top, left: r.right + 8 });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [flyoutGroup]);

  // Click-outside / escape closes the desktop flyout.
  useEffect(() => {
    if (!flyoutGroup) return;
    const onDown = (e) => {
      const fly = flyoutRef.current;
      const btn = groupButtonRefs.current[flyoutGroup];
      if (fly && fly.contains(e.target)) return;
      if (btn && btn.contains(e.target)) return;
      setFlyoutGroup(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setFlyoutGroup(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [flyoutGroup]);

  // Close the flyout whenever the route changes.
  useEffect(() => {
    setFlyoutGroup(null);
  }, [location.pathname]);

  // registerButton returns a stable ref-callback per groupKey. Caching the
  // per-key callbacks keeps the `buttonRef` prop identity stable across
  // Sidebar re-renders so NavGroupItem's React.memo barrier doesn't break.
  const refCallbacks = useRef({});
  const registerButton = useCallback((key) => {
    if (!refCallbacks.current[key]) {
      refCallbacks.current[key] = (el) => {
        groupButtonRefs.current[key] = el;
      };
    }
    return refCallbacks.current[key];
  }, []);

  return useMemo(
    () => ({ flyoutGroup, setFlyoutGroup, flyoutPos, flyoutRef, registerButton }),
    [flyoutGroup, flyoutPos, registerButton]
  );
}
